import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { Close } from "@mui/icons-material";
import PrimaryTextField from "../PrimaryTextField";
import { useEffect, useState } from "react";
import PrimaryButton from "../PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import {
  setSaveAsMapInstance,
  setSaveMapInstance,
} from "../../redux/slices/mapSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showLoader } from "../../redux/slices/loaderSlice";
import { getUserMapFolders } from "../../api";

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  color: "white",
}));

const SaveMapModal = ({ open, onSave }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState(false);

  const handleCreateFolder = () => {
    formik.setValues({ folderName: "", folderId: null });
    setNewFolder(true);
  };

  const handleClose = () => {
    dispatch(setSaveMapInstance(false));
    dispatch(setSaveAsMapInstance(false));
  };

  const fetchMapFolders = async () => {
    dispatch(
      showLoader({
        showLoader: true,
        loaderMessage: "Loading...",
      })
    );
    const res = await getUserMapFolders({ id: user.adminData.id });
    dispatch(showLoader({ showLoader: false }));
    if (!res.data) return;
    setFolders(
      res.data.userMapFolders.map((d) => ({
        key: d.name,
        value: d.name,
        id: d.id,
      }))
    );
  };

  const formik = useFormik({
    initialValues: {
      mapName: "",
      folderName: "",
      folderId: null,
    },
    validationSchema: Yup.object({
      mapName: Yup.string()
        .max(50)
        .min(1)
        .matches(
          /^[a-zA-Z0-9@_\- ]+$/,
          "Map name can only contain letters, numbers, and '@-_`"
        )
        .required("Map Name is required")
        .trim("Empty name is not acceptable"),
      folderName: Yup.string()
        .max(50)
        .min(1)
        .matches(
          /^[a-zA-Z0-9@_\- ]+$/,
          "Folder name can only contain letters, numbers, and '@-_`"
        )
        .required("Folder Name is required")
        .trim("Empty name is not acceptable"),
      folderId: Yup.string().optional().nullable(),
    }),
    onSubmit: (values) => {
      onSave(values);
    },
  });

  useEffect(() => {
    fetchMapFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"xs"}
      sx={{
        "& .MuiPaper-root": {
          background: palette.background.dark,
          border: `1px solid ${palette.text.white60}`,
          borderRadius: "13px",
        },
      }}
    >
      <DialogTitle sx={{ color: "white" }}>Save Map</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          {newFolder ? (
            <>
              <PrimaryTextField
                label="Folder Name"
                type="text"
                fullWidth
                margin="normal"
                variant="outlined"
                name="folderName"
                value={formik.values.folderName}
                onChange={formik.handleChange}
                error={
                  formik.touched.folderName && Boolean(formik.errors.folderName)
                }
                helperText={
                  formik.touched.folderName && formik.errors.folderName
                }
                sx={{
                  "& .MuiOutlinedInput-input": {
                    height: "auto",
                  },
                }}
              />
              <Button
                variant="text"
                color="primary"
                sx={{ mt: 1 }}
                onClick={() => setNewFolder(false)}
              >
                Select Folder
              </Button>
            </>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>Folder Name</InputLabel>
                <Select
                  sx={{ color: "white" }}
                  label="Folder Name"
                  name="folderName"
                  value={formik.values.folderName}
                  onChange={(e) => {
                    formik.setValues({
                      folderId: folders.find((d) => d.value === e.target.value)
                        ?.id,
                    });
                    formik.handleChange(e);
                  }}
                  error={
                    formik.touched.folderName &&
                    Boolean(formik.errors.folderName)
                  }
                >
                  {folders.map((d, i) => (
                    <CustomMenuItem key={i} value={d.value}>
                      {d.key}
                    </CustomMenuItem>
                  ))}
                </Select>
                {formik.touched.folderName && formik.errors.folderName && (
                  <FormHelperText sx={{ color: "red" }}>
                    {formik.errors.folderName}
                  </FormHelperText>
                )}
              </FormControl>
              <Button
                variant="text"
                color="primary"
                sx={{ mt: 1 }}
                onClick={handleCreateFolder}
              >
                Create Folder +
              </Button>
            </>
          )}

          <PrimaryTextField
            label="Map Name"
            type="text"
            fullWidth
            margin="normal"
            variant="outlined"
            id="name"
            name="mapName"
            value={formik.values.mapName}
            onChange={formik.handleChange}
            error={formik.touched.mapName && Boolean(formik.errors.mapName)}
            helperText={formik.touched.mapName && formik.errors.mapName}
            sx={{
              "& .MuiOutlinedInput-input": {
                height: "auto",
              },
            }}
          />

          <Box
            sx={{
              width: "100%",
              pt: "20px",
              display: "flex",
              justifyContent: "end",
            }}
          >
            <PrimaryButton
              variant="contained"
              color="primary"
              style={{
                fontWeight: 600,
                fontSize: 18,
                borderRadius: "7px",
                boxShadow: "1px 3px 12px black",
              }}
              type="submit"
            >
              Save
            </PrimaryButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SaveMapModal;
